/**
 * Radio element driver (plan P2b). FOM sends no CORS header, so the radio plays through one shared
 * HTMLAudioElement, outside Web Audio. Race time drives it: inside [t_play, t_play + dur) the target is
 * offset_s + (raceT - t_play), re-set on > DRIFT_S drift; outside the window the element is paused. That
 * one rule covers seek, mode switch, hold, buffering, drag and a hidden tab. `element.volume` is never
 * touched (read-only on iOS): the facade ducks the synth buses instead.
 */
import { DRIFT_S, radioTarget } from './policy.ts';
import type { RadioClip, RadioFile } from './types.ts';

export interface AudioElementLike {
  src: string;
  currentTime: number;
  muted: boolean;
  readonly paused: boolean;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: 'error', fn: () => void): void;
}

export class RadioElementDriver {
  private clips: RadioClip[] = [];
  private audible = false;
  private loaded = '';
  private active: RadioClip | null = null;
  private starting = false;
  readonly unavailable = new Set<string>();

  constructor(private readonly el: AudioElementLike) {
    el.addEventListener('error', () => { if (this.active) this.unavailable.add(this.active.id); });
  }

  setFile(file: RadioFile | null): void {
    this.pause();
    this.unavailable.clear();
    this.audible = file?.lag.granularity === 'second';
    this.clips = file ? file.clips.filter((c) => c.src_url).sort((a, b) => a.t_play - b.t_play) : [];
  }

  /** on the sound-toggle gesture: a muted play()/pause() lets later programmatic play() calls through */
  unlock(): void {
    this.el.muted = true;
    this.el.play().then(() => { this.el.pause(); this.el.muted = false; }, () => { this.el.muted = false; });
  }

  private pause(): void {
    if (!this.el.paused) this.el.pause();
    this.active = null;
  }

  /** once per frame; `allowed` is the rate policy's radioAudio. Returns the clip being played, or null. */
  update(raceT: number, allowed: boolean): RadioClip | null {
    const clip = allowed && this.audible ? this.clips.find((c) => !this.unavailable.has(c.id) && radioTarget(raceT, c) !== null) ?? null : null;
    const target = clip ? radioTarget(raceT, clip) : null;
    if (!clip || target === null || !clip.src_url) { this.pause(); return null; }
    this.active = clip;
    if (this.loaded !== clip.src_url) { this.el.src = clip.src_url; this.loaded = clip.src_url; this.el.currentTime = target; }
    else if (Math.abs(this.el.currentTime - target) > DRIFT_S) this.el.currentTime = target;
    if (this.el.paused && !this.starting) {
      this.starting = true;
      const id = clip.id;
      this.el.play().then(() => { this.starting = false; }, (e: unknown) => {
        this.starting = false;
        const name = e instanceof Error ? e.name : '';
        // AbortError: a pause() interrupted it. NotAllowedError: not unlocked yet, retry next frame.
        if (name !== 'AbortError' && name !== 'NotAllowedError') this.unavailable.add(id);
      });
    }
    return clip;
  }

  get playing(): boolean { return this.active !== null && !this.el.paused; }
}
